import React, { useState } from 'react';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { cognitoAuth } from '@/services/cognitoAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { logger, ValidationError, NetworkError } from '@/utils/logger';

export const SettingsPage: React.FC = () => {
  const { user } = useCustomerAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);

    try {
      await cognitoAuth.changePassword(currentPassword, newPassword);

      logger.info('Password changed successfully');
      setPasswordSuccess('Password changed successfully!');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setPasswordSuccess('');
      }, 5000);

    } catch (error) {
      logger.error('Password change failed:', error);

      if (error instanceof ValidationError) {
        setPasswordError(error.message);
      } else if (error instanceof NetworkError) {
        setPasswordError(error.message);
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Settings</h1>
        <p className="text-sm text-slate-600">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-4">
        {/* Profile Information */}
        <Card className="p-4">
          <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile Information
          </h2>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Full Name
              </label>
              <Input
                type="text"
                defaultValue={user?.name || ''}
                placeholder="John Doe"
                className="h-9 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Email Address
              </label>
              <Input
                type="email"
                defaultValue={user?.email || ''}
                placeholder="john@example.com"
                disabled
                className="h-9 text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Contact support to change your email address
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Company Name
              </label>
              <Input
                type="text"
                defaultValue={user?.companyName || ''}
                placeholder="Acme Corporation"
                className="h-9 text-sm"
              />
            </div>

            <Button size="sm" className="h-8 text-xs">Save Changes</Button>
          </div>
        </Card>

        {/* Password */}
        <Card className="p-4">
          <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Password
          </h2>

          <p className="text-xs text-slate-600 mb-3">
            Change your password to keep your account secure
          </p>

          <form onSubmit={handlePasswordChange}>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Current Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter current password"
                  className="h-9 text-sm"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isChangingPassword}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  New Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  className="h-9 text-sm"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isChangingPassword}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  className="h-9 text-sm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isChangingPassword}
                />
              </div>

              {passwordError && (
                <div className="flex items-start gap-1.5 p-2 bg-red-50 rounded border border-red-200">
                  <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">
                    {passwordError}
                  </p>
                </div>
              )}

              {passwordSuccess && (
                <div className="flex items-start gap-1.5 p-2 bg-green-50 rounded border border-green-200">
                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-800">
                    {passwordSuccess}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                size="sm"
                className="h-8 text-xs"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? 'Updating Password...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

